import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { listAdminLureTypes, createLureType, updateLureType, setLureTypeActive } from '../../api/admin';
import { LureTypeFormDialog } from './LureTypeFormDialog';
import type { AdminLureTypeResponse } from '../../types/api';

export function AdminLureTypesTab() {
  const queryClient = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<AdminLureTypeResponse | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin-luretypes'],
    queryFn: listAdminLureTypes,
  });

  const toggleActive = useMutation({
    mutationFn: ({ id, active }: { id: number; active: boolean }) => setLureTypeActive(id, active),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin-luretypes'] }),
  });

  function openNew() {
    setEditing(null);
    setDialogOpen(true);
  }

  function openEdit(lt: AdminLureTypeResponse) {
    setEditing(lt);
    setDialogOpen(true);
  }

  async function handleSave(name: string) {
    if (editing) {
      await updateLureType(editing.id, { name });
    } else {
      await createLureType({ name });
    }
    queryClient.invalidateQueries({ queryKey: ['admin-luretypes'] });
  }

  if (isLoading) return <div className="p-4">Loading lures…</div>;
  if (error) return <div className="p-4 text-red-500">Failed to load lures.</div>;

  return (
    <div>
      <div className="mb-4 flex justify-end">
        <button
          type="button"
          onClick={openNew}
          className="rounded-md bg-teal-600 hover:bg-teal-700 px-4 py-2 text-sm font-medium text-white"
        >
          Add Lure
        </button>
      </div>

      {!data || data.length === 0 ? (
        <div className="p-4 text-slate-500">No lures.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="text-left text-xs uppercase text-slate-500 dark:text-slate-400">
              <tr>
                <th className="px-3 py-2">Name</th>
                <th className="px-3 py-2">Catches</th>
                <th className="px-3 py-2">Active</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
              {data.map(lt => (
                <tr key={lt.id} className={lt.active ? '' : 'opacity-50'}>
                  <td className="px-3 py-2 font-medium text-slate-900 dark:text-slate-100">{lt.name}</td>
                  <td className="px-3 py-2 text-slate-600 dark:text-slate-300">{lt.catchCount}</td>
                  <td className="px-3 py-2">
                    <button
                      type="button"
                      onClick={() => toggleActive.mutate({ id: lt.id, active: !lt.active })}
                      className={`rounded px-2 py-0.5 text-xs font-medium ${
                        lt.active
                          ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                          : 'bg-slate-100 text-slate-500 dark:bg-slate-700 dark:text-slate-400'
                      }`}
                    >
                      {lt.active ? 'Active' : 'Inactive'}
                    </button>
                  </td>
                  <td className="px-3 py-2">
                    <button
                      type="button"
                      onClick={() => openEdit(lt)}
                      className="text-xs text-teal-600 hover:underline dark:text-teal-400"
                    >
                      Edit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <LureTypeFormDialog
        open={dialogOpen}
        lureType={editing}
        onClose={() => setDialogOpen(false)}
        onSave={handleSave}
      />
    </div>
  );
}
